import type { SourceStatus } from './types';

/**
 * Resolve a read into "it worked", "it worked and there was nothing", or "it
 * failed" — instead of collapsing the last two into an empty array.
 *
 * The dashboard already draws this distinction (see SourceStatus in ./types),
 * but the pages outside it did not: ReportsPage, QuarterlyReportDetailPage and
 * MelioIntelligencePage all swallowed read errors and rendered their empty
 * state, so a Firestore rules regression told a member "your first quarterly
 * report will appear here" while their report existed and was simply
 * unreadable. That is the worst possible failure mode for a clinical record —
 * silence that looks like an answer.
 *
 * `isEmpty` decides what counts as nothing for this particular read; the
 * default treats an empty array or a null/undefined result as empty.
 */
export interface Settled<T> {
  status: SourceStatus;
  data: T | null;
  error: unknown;
}

export async function settle<T>(
  promise: Promise<T>,
  isEmpty: (value: T) => boolean = defaultIsEmpty,
): Promise<Settled<T>> {
  try {
    const data = await promise;
    return { status: isEmpty(data) ? 'empty' : 'ok', data, error: null };
  } catch (error) {
    // Deliberately logged, not swallowed: a failed member read should be
    // visible in the console during support calls as well as on screen.
    console.error('Member read failed:', error);
    return { status: 'failed', data: null, error };
  }
}

function defaultIsEmpty(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
}
